import { useEffect, useMemo, useState } from "react";

import { OURS_EVENTS, trackOursEvent } from "~/providers";
import { ROUTES } from "~/router";
import {
  BOOKING_FLOW_EVENTS,
  PATIENT_STATUS,
  useBookingFlowEvents,
  useConditionalScreeningMutation,
  useInsurancesListQuery,
  useStatesListQuery,
} from "~/services";
import { EmailInput, useExistingEmailRedirection, useRedirect } from "~/shared";
import { useBookingFlowStore, usePatientStore } from "~/stores";
import { errorToast } from "~/ui";
import { validateError } from "~/utils";

export const SchedulingEmailInput = () => {
  const redirect = useRedirect();
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const patientVisitData = usePatientStore((state) =>
    state.getPatientVisitData(),
  );
  const stateId = patientVisitData?.patientStateId;
  const insuranceId = patientVisitData?.insuranceId;
  const { data: states } = useStatesListQuery();
  const { data: insurances } = useInsurancesListQuery({
    stateId: stateId ?? null,
  });

  const state = useMemo(
    () => states?.find((s) => s.id === stateId),
    [states, stateId],
  );
  const insurance = useMemo(
    () => insurances?.find((i) => i.id === insuranceId),
    [insurances, insuranceId],
  );

  const {
    mutate: saveScreening,
    error,
    isSuccess,
    isPending,
    isError,
  } = useConditionalScreeningMutation();

  const { handleExistingEmailRedirection, isInvalidEmail } =
    useExistingEmailRedirection({
      error,
      routeToRedirect: ROUTES.hubMeetProviderPatientInformation,
      email,
    });

  const updatePatientVisitData = usePatientStore(
    (state) => state.updatePatientVisitData,
  );

  const bookingFlow = useBookingFlowStore((state) => state.flow);
  const { trackTimeEvent } = useBookingFlowEvents();

  useEffect(() => {
    trackTimeEvent(BOOKING_FLOW_EVENTS.EMAIL_CONTINUE_PAGE_VIEWED_AT);
  }, []);

  useEffect(() => {
    if (!error) {
      setErrorMessage(undefined);
      return;
    }

    const parsedError = validateError(error);
    if (!parsedError) {
      errorToast();
      return;
    }

    const emailFieldErrors = parsedError?.response?.data?.error?.fields?.email;

    if (isInvalidEmail || emailFieldErrors?.length) {
      setErrorMessage(
        emailFieldErrors?.join(",\n") ?? "Please enter a valid email address.",
      );
      return;
    }
  }, [error, isInvalidEmail]);

  const handleSubmit = async (emailValue: string) => {
    setEmail(emailValue);

    updatePatientVisitData({
      patientEmail: emailValue,
    });

    if (!bookingFlow || !state) {
      redirect(ROUTES.getStarted);
      return;
    }

    saveScreening({
      bookingFlowId: bookingFlow.id,
      formValues: {
        state,
        insurance: insurance ?? null,
        email: emailValue,
        patientType: PATIENT_STATUS.NEW,
        answers: {
          insuranceNotListed: null,
          insuranceFreeText: null,
        },
      },
    });
  };

  const handleEmailBlur = (emailValue: string) => {
    if (!emailValue) {
      return;
    }

    trackOursEvent(OURS_EVENTS.EMAIL_ENTRY, {
      email: emailValue,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      redirect(ROUTES.schedulingPatientInformation);
      return;
    }

    if (isError) {
      handleExistingEmailRedirection();
    }
  }, [handleExistingEmailRedirection, isError, isSuccess, redirect]);

  return (
    <EmailInput
      backRoute={ROUTES.schedulingInitialVisit}
      title="Where should we send your appointment confirmation?"
      onSubmit={handleSubmit}
      onBlur={handleEmailBlur}
      externalError={errorMessage}
      isLoading={isPending}
    />
  );
};
